import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '@/services/api';
import { Mail, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import ThemeToggle from '@/components/shared/ThemeToggle';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [devLink, setDevLink] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const r = await authAPI.forgotPassword(email);
      setSent(true);
      // Dev/mock helper — when email delivery isn't wired, show the link inline
      if (r.reset_link_dev) setDevLink(r.reset_link_dev);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex items-center justify-center px-6" data-testid="forgot-password-page">
      <ThemeToggle className="fixed top-4 right-4 z-50" />
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-slate-500 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300 text-sm mb-8" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        <h1 className="text-3xl font-bold mb-2" style={{fontFamily: "'DM Serif Display', serif"}}>
          <span className="text-slate-900 dark:text-white">Reset your </span>
          <span className="text-emerald-600 dark:text-emerald-400">password</span>
        </h1>
        <p className="text-slate-500 dark:text-zinc-500 mb-8 text-sm">Enter your email and we&apos;ll send you a link to reset your password.</p>

        {!sent ? (
          <form onSubmit={onSubmit} className="space-y-5">
            {error && <div className="bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="forgot-error">{error}</div>}
            <div>
              <label className="block text-sm text-slate-500 dark:text-zinc-400 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500" />
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit-btn"
              className="w-full bg-slate-900 dark:bg-white text-white dark:text-black font-medium py-2.5 rounded-lg hover:bg-slate-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send reset link'}
            </button>
          </form>
        ) : (
          <div className="space-y-4" data-testid="forgot-success">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-5">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400 mb-2" />
              <p className="text-emerald-700 dark:text-emerald-300 text-sm font-medium">Check your inbox</p>
              <p className="text-slate-500 dark:text-zinc-400 text-sm mt-1">If an account exists for <span className="text-slate-900 dark:text-white">{email}</span>, we&apos;ve sent a reset link. It expires in 1 hour.</p>
              <p className="text-slate-400 dark:text-zinc-500 text-xs mt-3" data-testid="forgot-hint">Didn&apos;t get an email? Double-check this is the exact address you signed up with — for security, we don&apos;t send an email (or an error) for addresses that aren&apos;t registered. Also check Spam/Junk.</p>
            </div>
            {devLink && (
              <div className="bg-slate-50 dark:bg-zinc-900/60 border border-slate-200 dark:border-zinc-800 rounded-lg p-4 text-xs">
                <p className="text-slate-500 dark:text-zinc-500 mb-1">Dev mode (email is mocked):</p>
                <Link to={devLink} className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 break-all" data-testid="dev-reset-link">{devLink}</Link>
              </div>
            )}
            <Link to="/login" className="block text-center text-sm text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white">Back to sign in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
